import React, { useState, useEffect } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faEdit } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useAlterarConta } from 'hooks/useContas';
import { ContaType, AlterarContaDto } from 'services/contaService';

interface ModalEditarContaProps {
    show: boolean;
    onHide: () => void;
    conta: ContaType | null;
}

const ModalEditarConta: React.FC<ModalEditarContaProps> = ({ show, onHide, conta }) => {
    const [nome, setNome] = useState('');
    const [erro, setErro] = useState('');

    const alterarContaMutation = useAlterarConta();

    useEffect(() => {
        if (conta) {
            setNome(conta.nome);
        }
    }, [conta]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErro('');

        if (!nome.trim()) {
            setErro('O nome da conta é obrigatório');
            return;
        }

        if (!conta) {
            setErro('Conta não encontrada');
            return;
        }

        try {
            const dto: AlterarContaDto = { nome: nome.trim() };
            await alterarContaMutation.mutateAsync({ id: conta.id, dto });

            onHide();
        } catch (error: any) {
            setErro(error?.response?.data?.message || 'Erro ao alterar conta');
        }
    };

    const handleClose = () => {
        setNome(conta?.nome || '');
        setErro('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faEdit} className="me-2 text-primary-emphasis" />
                    Editar Conta
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {erro && (
                        <Alert variant="danger" className="mb-3 border border-danger border-opacity-25 bg-danger-subtle">
                            <div className="text-danger-emphasis">{erro}</div>
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Nome da Conta *</Form.Label>
                        <Form.Control
                            type="text"
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            placeholder="Ex: Conta Corrente, Poupança, Caixa..."
                            required
                            disabled={alterarContaMutation.isPending}
                        />
                        <Form.Text className="text-body-secondary">
                            Digite um nome descritivo para identificar esta conta
                        </Form.Text>
                    </Form.Group>

                    {conta && (
                        <Alert variant="info" className="small border border-info border-opacity-25 bg-info-subtle">
                            <div className="text-info-emphasis">
                                <FontAwesomeIcon icon={faWallet} className="me-2" />
                                Saldo atual: <strong>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(conta.saldo)}</strong>
                            </div>
                        </Alert>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={alterarContaMutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={alterarContaMutation.isPending || !nome.trim() || nome === conta?.nome}
                    >
                        {alterarContaMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                Salvar Alterações
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ModalEditarConta; 